/** Rate grid table component with inline editing and pagination. */
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import apiClient, { API_BASE_URL } from "../../api/client";
import IncrementField from "../common/IncrementField";
import { effPPM } from "../../utils/rating";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Alert,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  createColumnHelper,
  SortingState,
  flexRender,
} from "@tanstack/react-table";

/** Rate interface from API. */
export interface Rate {
  destination_prefix: string;
  rate: number;
  connect_fee: number;
  billing_increment: number;
  effective_date: string;
}

/** Props for RatePlanTable. */
interface RatePlanTableProps {
  /** Rate plan ID to fetch rates for */
  ratePlanId: string;
  /** Trigger refresh when this value changes */
  refreshTrigger?: number;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<Rate>();

/** Rate Plan Table component. */
export default function RatePlanTable({ ratePlanId, refreshTrigger = 0 }: RatePlanTableProps) {
  const [rates, setRates] = useState<Rate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "destination_prefix", desc: false }]);
  const [pagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<Rate>>({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /** Fetch rates from API. */
  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = pagination.pageIndex + 1;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pagination.pageSize.toString(),
      });

      if (filter) {
        params.append("destination_prefix", filter);
      }

      const response = await apiClient.get(
        `${API_BASE_URL}/api/v1/rate-plans/${ratePlanId}/rates?${params.toString()}`
      );

      const data = response.data;
      setRates(data.rates || []);
      setTotalCount(data.total || 0);
    } catch (err: any) {
      setError(err.message || "Failed to load rates");
    } finally {
      setLoading(false);
    }
  }, [ratePlanId, pagination.pageIndex, pagination.pageSize, filter]);

  /** Debounced save function. */
  const debouncedSave = useCallback(
    (destinationPrefix: string, updatedRate: Partial<Rate>) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(async () => {
        setSaving(true);
        try {
          // CSRF: added automatically by the request interceptor
          await apiClient.put(
            `${API_BASE_URL}/api/v1/rate-plans/${ratePlanId}/rates`,
            {
              destination_prefix: destinationPrefix,
              ...updatedRate,
            }
          );

          // Optimistic update already done, just clear saving state
          setError(null);
        } catch (err: any) {
          setError(err?.response?.data?.detail || err.message);
          // Revert on error by refetching
          fetchRates();
        } finally {
          setSaving(false);
        }
      }, 500); // 500ms debounce
    },
    [ratePlanId, fetchRates]
  );

  /** Add Rate dialog state. */
  const [addOpen, setAddOpen] = useState(false);
  const [newRate, setNewRate] = useState({
    destination_prefix: "",
    rate: "",
    connect_fee: "0",
    billing_increment: "1",
  });
  const [addError, setAddError] = useState("");

  /** Create a new rate via the upsert endpoint. */
  const handleAddRate = async () => {
    const prefix = newRate.destination_prefix.trim();
    const rate = parseFloat(newRate.rate);
    if (!prefix) {
      setAddError("Destination prefix is required");
      return;
    }
    if (!/^[0-9.*]+$/.test(prefix)) {
      setAddError("Prefix must be digits with optional .* wildcard");
      return;
    }
    if (!(rate >= 0.01)) {
      setAddError("Rate must be at least $0.01");
      return;
    }
    setSaving(true);
    setAddError("");
    try {
      // CSRF: added automatically by the request interceptor
      await apiClient.put(
        `${API_BASE_URL}/api/v1/rate-plans/${ratePlanId}/rates`,
        {
          destination_prefix: prefix,
          rate,
          connect_fee: parseFloat(newRate.connect_fee) || 0,
          billing_increment: parseInt(newRate.billing_increment) || 1,
        }
      );
      setAddOpen(false);
      setNewRate({ destination_prefix: "", rate: "", connect_fee: "0", billing_increment: "1" });
      await fetchRates();
    } catch (err: any) {
      const errData = err?.response?.data;
      setAddError(
        typeof errData?.detail === "string"
          ? errData.detail
          : errData?.detail?.[0]?.msg || err.message || "Failed to add rate"
      );
    } finally {
      setSaving(false);
    }
  };

  /** Start editing a row. */
  const handleStartEdit = (rate: Rate) => {
    setEditingRow(rate.destination_prefix);
    setEditData(rate);
  };

  /** Cancel editing. */
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  /** Save edit. */
  const handleSaveEdit = () => {
    if (!editingRow) return;

    // Optimistic update
    setRates((prev) =>
      prev.map((r) =>
        r.destination_prefix === editingRow
          ? { ...r, ...editData } as Rate
          : r
      )
    );

    debouncedSave(editingRow, editData);
    setEditingRow(null);
    setEditData({});
  };

  /** Delete rate. */
  const handleDelete = async (destinationPrefix: string) => {
    setDeleting((prev) => new Set(prev).add(destinationPrefix));
    try {
      // CSRF: added automatically by the request interceptor
      await apiClient.delete(
        `${API_BASE_URL}/api/v1/rate-plans/${ratePlanId}/rates/${encodeURIComponent(destinationPrefix)}`
      );

      // Optimistic update
      setRates((prev) => prev.filter((r) => r.destination_prefix !== destinationPrefix));
      setTotalCount((prev) => prev - 1);
      setSelectedRows((prev) => {
        const next = new Set(prev);
        next.delete(destinationPrefix);
        return next;
      });
    } catch (err: any) {
      setError(err.message);
      fetchRates(); // Revert on error
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(destinationPrefix);
        return next;
      });
    }
  };

  /** Bulk delete selected rates. */
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;

    // CSRF: added automatically by the request interceptor (raw fetch omitted
    // it here and would have been rejected 403 by CSRFMiddleware)
    const promises = Array.from(selectedRows).map((prefix) =>
      apiClient.delete(
        `${API_BASE_URL}/api/v1/rate-plans/${ratePlanId}/rates/${encodeURIComponent(prefix)}`
      )
    );

    try {
      await Promise.all(promises);
      setRates((prev) => prev.filter((r) => !selectedRows.has(r.destination_prefix)));
      setTotalCount((prev) => prev - selectedRows.size);
      setSelectedRows(new Set());
    } catch (err: any) {
      setError("Failed to delete some rates");
      fetchRates(); // Revert on error
    }
  };

  /** Fetch rates when dependencies change. */
  useEffect(() => {
    fetchRates();
  }, [fetchRates, refreshTrigger]);

  /** Define table columns. */
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: ({ table }) => (
          <Checkbox
            indeterminate={
              table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            }
            checked={table.getIsAllRowsSelected()}
            onChange={(e) => {
              const checked = e.target.checked;
              setSelectedRows(
                checked
                  ? new Set(rates.map((r) => r.destination_prefix))
                  : new Set()
              );
            }}
          />
        ),
        cell: (props) => (
          <Checkbox
            checked={selectedRows.has(props.row.original.destination_prefix)}
            onChange={(e) => {
              const prefix = props.row.original.destination_prefix;
              setSelectedRows((prev) => {
                const next = new Set(prev);
                if (e.target.checked) {
                  next.add(prefix);
                } else {
                  next.delete(prefix);
                }
                return next;
              });
            }}
          />
        ),
      }),
      columnHelper.accessor("destination_prefix", {
        header: "Destination Prefix",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("rate", {
        header: "Rate",
        cell: (info) => {
          const isEditing = editingRow === info.row.original.destination_prefix;
          if (isEditing) {
            return (
              <TextField
                type="number"
                value={editData.rate ?? info.getValue()}
                onChange={(e) =>
                  setEditData((prev) => ({
                    ...prev,
                    rate: parseFloat(e.target.value) || 0,
                  }))
                }
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: 0.0001, min: 0 },
                }}
                sx={{ width: 120 }}
              />
            );
          }
          return `$${info.getValue().toFixed(4)}`;
        },
      }),
      columnHelper.accessor("connect_fee", {
        header: "Connect Fee",
        cell: (info) => {
          const isEditing = editingRow === info.row.original.destination_prefix;
          if (isEditing) {
            return (
              <TextField
                type="number"
                value={editData.connect_fee ?? info.getValue()}
                onChange={(e) =>
                  setEditData((prev) => ({
                    ...prev,
                    connect_fee: parseFloat(e.target.value) || 0,
                  }))
                }
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { step: 0.01, min: 0 },
                }}
                sx={{ width: 100 }}
              />
            );
          }
          return `$${info.getValue().toFixed(2)}`;
        },
      }),
      columnHelper.accessor("billing_increment", {
        header: "Billing Increment",
        cell: (info) => {
          const isEditing = editingRow === info.row.original.destination_prefix;
          if (isEditing) {
            return (
              <IncrementField
                label="Increment"
                value={String(editData.billing_increment ?? info.getValue())}
                onChange={(v) =>
                  setEditData((prev) => ({
                    ...prev,
                    billing_increment: parseInt(v, 10) || 1,
                  }))
                }
              />
            );
          }
          return `${info.getValue()}s`;
        },
      }),
      columnHelper.display({
        id: "eff_ppm",
        header: "Eff. PPM @60s",
        cell: (info) => {
          const r = info.row.original;
          const v = effPPM(Number(r.rate), Number(r.connect_fee || 0), Number(r.billing_increment) || 1);
          return (
            <Tooltip title="Price of a 60s call incl. connect fee, per minute (increment-aware)">
              <span>{v.toFixed(6)}</span>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor("effective_date", {
        header: "Effective Date",
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (props) => {
          const rate = props.row.original;
          const isEditing = editingRow === rate.destination_prefix;
          const isDeleting = deleting.has(rate.destination_prefix);

          if (isEditing) {
            return (
              <Box>
                <Tooltip title="Save">
                  <IconButton
                    size="small"
                    onClick={handleSaveEdit}
                    color="primary"
                    disabled={saving}
                   aria-label="Save">
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={handleCancelEdit}
                    color="error"
                   aria-label="Cancel">
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          }

          return (
            <Box>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => handleStartEdit(rate)}
                  color="primary"
                  disabled={!!editingRow || isDeleting}
                 aria-label="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(rate.destination_prefix)}
                  color="error"
                  disabled={!!editingRow || isDeleting}
                 aria-label="Delete">
                  {isDeleting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      }),
    ],
    [rates, editingRow, editData, selectedRows, deleting, saving]
  );

  /** Initialize TanStack Table. */
  const table = useReactTable({
    data: rates,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      pagination,
    },
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    manualPagination: true,
  });

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter and Bulk Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}>
        <TextField
          placeholder="Filter by destination prefix..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {selectedRows.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedRows.size})
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddError("");
              setAddOpen(true);
            }}
          >
            Add Rate
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {loading && rates.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Rate Table */}
      {!loading || rates.length > 0 ? (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell key={header.id}>
                        {header.column.getCanSort() ? (
                          <TableSortLabel
                            active={header.column.getIsSorted() !== false}
                            direction={
                              header.column.getIsSorted() === "asc" ? "asc" : "desc"
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </TableSortLabel>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography variant="body1" sx={{ py: 4 }}>
                        {filter
                          ? "No rates found matching the filter."
                          : "No rates yet. Upload a CSV to get started."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        bgcolor:
                          editingRow === row.original.destination_prefix
                            ? "action.selected"
                            : "inherit",
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
            <Typography variant="body2">
              Showing {pagination.pageIndex * pagination.pageSize + 1} to{" "}
              {Math.min(
                (pagination.pageIndex + 1) * pagination.pageSize,
                totalCount
              )}{" "}
              of {totalCount} rates
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => table.setPageIndex(0)}
                disabled={pagination.pageIndex === 0}
              >
                First
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => table.previousPage()}
                disabled={pagination.pageIndex === 0}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => table.nextPage()}
                disabled={
                  table.getPageCount() === 0 ||
                  pagination.pageIndex >= table.getPageCount() - 1
                }
              >
                Next
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={
                  table.getPageCount() === 0 ||
                  pagination.pageIndex >= table.getPageCount() - 1
                }
              >
                Last
              </Button>
            </Box>
          </Box>
        </Paper>
      ) : null}

      {saving && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1, textAlign: "center" }}
        >
          Saving changes...
        </Typography>
      )}
      {/* Add Rate dialog */}
      <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Rate</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {addError && <Alert severity="error">{addError}</Alert>}
            <TextField
              label="Destination Prefix"
              required
              fullWidth
              value={newRate.destination_prefix}
              onChange={(e) => setNewRate({ ...newRate, destination_prefix: e.target.value })}
              placeholder="e.g. 44 or 86.*"
              helperText="Digits with optional .* wildcard"
              autoFocus
            />
            <TextField
              label="Rate (USD/min)"
              required
              fullWidth
              type="number"
              inputProps={{ step: 0.0001, min: 0.01 }}
              value={newRate.rate}
              onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })}
              InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
              helperText="Minimum $0.01"
            />
            <TextField
              label="Connect Fee (USD)"
              fullWidth
              type="number"
              inputProps={{ step: 0.0001, min: 0 }}
              value={newRate.connect_fee}
              onChange={(e) => setNewRate({ ...newRate, connect_fee: e.target.value })}
              InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
            />
            <IncrementField
              value={newRate.billing_increment}
              onChange={(v) => setNewRate({ ...newRate, billing_increment: v })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleAddRate} variant="contained" disabled={saving}>
            Add Rate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
